import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {memo} from 'react';

import {countActiveFilters} from '../../../../hooks/useFilterCategorySelection';
import type {EagColumn} from '../../../../types/EagColumn';
import type {EagColumnOrGroup} from '../../../../types/EagColumnGroup';
import type {EagFilterProps} from '../../../../types/EagFilterProps';
import type {Filters} from '../../../../types/Filters';
import type {FilterState} from '../../../../types/FilterState';
import {renderFilter} from '../../../../utils/renderFilter';
import {getVisibleFilterColumns} from './utils/getVisibleFilterColumns';

const FULL_WIDTH_FILTER_TYPES = new Set(['DATE_RANGE', 'DATE_TIME_RANGE', 'SLIDER_RANGE', 'RANGE']);

type FilterContentAreaProps = {
  columnConfig: EagColumnOrGroup[];
  selectedCategoryId: string | null;
  filterState: Filters;
  onFilterChange: (key: string, value: unknown) => void;
  searchTerm: string;
  noScroll?: boolean;
  singleColumn?: boolean;
  /** When true, only filters with active values are shown. */
  showActiveOnly?: boolean;
};

type FilterItemProps = {
  column: EagColumn;
  value: FilterState;
  isActive: boolean;
  onFilterChange: (key: string, value: unknown) => void;
};

const FilterItem = memo((props: FilterItemProps) => {
  const onChange = (value: FilterState) => props.onFilterChange(props.column.key, value);

  const filterProps: EagFilterProps = {
    value: props.value ?? {},
    onChange,
    isDisabled: false,
    column: props.column,
  };

  return (
    <div className="flex flex-col gap-1.5" data-filter={props.column.key}>
      <label className="flex items-center gap-1.5 text-[12px] text-[#42526E]">
        <span className="truncate">{props.column.headerName}</span>
        <Show when={props.isActive}>
          <span
            className="flex-shrink-0 rounded-full bg-[#0065FF]"
            style={{width: '6px', height: '6px'}}
            data-testid={`filter-active-dot-${props.column.key}`}
          />
        </Show>
      </label>
      <div className="[&>*]:gap-1.5">{renderFilter(props.column, filterProps)}</div>
    </div>
  );
});

/**
 * Right content area of the 75vw FilterPanel.
 *
 * Renders filter widgets for the currently selected category in a responsive
 * 2-column grid.  Wider filter types (DateRange, DateTimeRange, SliderRange,
 * Range) automatically span both columns so their inputs have enough space.
 *
 * ## Category resolution
 *
 * When `selectedCategoryId` matches a group's `groupId`, all instances of that
 * group in `columnConfig` are collected (EagGrid allows the same `groupId` to
 * appear multiple times as a way to organise columns across multiple groups).
 * The union of their children forms the filterable column set.
 *
 * The special virtual id `'__standalone__'` collects all top-level columns
 * that are not part of any group.
 *
 * ## Search
 *
 * When `searchTerm` is non-empty, filter items whose `headerName` does not
 * contain the term (case-insensitive) are hidden.
 */
export function FilterContentArea(props: FilterContentAreaProps) {
  const t = useTranslationContext();

  const allFilterColumns = getVisibleFilterColumns(
    props.columnConfig,
    props.selectedCategoryId,
    props.searchTerm
  );

  const visibleFilterColumns = props.showActiveOnly
    ? allFilterColumns.filter((col) => countActiveFilters([col], props.filterState) > 0)
    : allFilterColumns;

  if (!props.selectedCategoryId) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400 p-8">
        {t('page.dataGrid.filter.selectCategoryPrompt')}
      </div>
    );
  }

  if (visibleFilterColumns.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400 p-8">
        {props.searchTerm
          ? t('page.dataGrid.filter.noFiltersMatchSearch')
          : t('page.dataGrid.filter.noFiltersAvailable')}
      </div>
    );
  }

  if (props.singleColumn) {
    return (
      <div className="flex flex-col gap-3">
        {visibleFilterColumns.map((column) => (
          <FilterItem
            key={column.key}
            column={column}
            value={props.filterState[column.key] ?? {}}
            isActive={countActiveFilters([column], props.filterState) > 0}
            onFilterChange={props.onFilterChange}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={props.noScroll ? 'p-4' : 'p-4 overflow-y-auto h-full'}>
      <div className="grid grid-cols-2 gap-4">
        {visibleFilterColumns.map((column) => {
          const isFullWidth = column.filter && FULL_WIDTH_FILTER_TYPES.has(column.filter.type);
          return (
            <div key={column.key} className={isFullWidth ? 'col-span-2' : 'col-span-1'}>
              <FilterItem
                column={column}
                value={props.filterState[column.key] ?? {}}
                isActive={countActiveFilters([column], props.filterState) > 0}
                onFilterChange={props.onFilterChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
